import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Lock, Star } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import BottomNav from "../components/BottomNav";
import { useNotifications } from "../context/NotificationContext";

const MatchModal = ({ profile, onClose, onChat }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    className="fixed inset-0 z-[60] bg-kora-ink/80 backdrop-blur-sm flex items-center justify-center p-6"
    data-testid="likes-match-modal"
  >
    <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
      <p className="font-display font-black text-3xl sm:text-4xl text-gradient-warm whitespace-nowrap">C'est un match&nbsp;!</p>
      <p className="text-kora-ink/60 mt-2">Vous et {profile.name} vous êtes plu.</p>
      <img src={resolveImg(profile.photo_url)} alt={profile.name} className="w-28 h-28 rounded-full object-cover mx-auto mt-5 border-4 border-kora-terracotta" />
      <button data-testid="likes-match-chat-btn" onClick={onChat} className="w-full mt-6 bg-kora-terracotta text-white rounded-full py-4 font-bold hover:bg-[#C94F2B] transition-colors">
        Envoyer un message
      </button>
      <button data-testid="likes-match-close-btn" onClick={onClose} className="w-full mt-2 text-kora-muted py-2 font-medium">Continuer</button>
    </motion.div>
  </motion.div>
);

const Likes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ count: 0, premium: false, profiles: [] });
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(null);
  const { markSeen } = useNotifications() || {};

  const load = async () => {
    try {
      const { data } = await api.get("/likes/received");
      setData(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); markSeen?.("likes"); }, []);

  const likeBack = async (p) => {
    if (busy) return;
    if (!data.premium) {
      toast.error("Matcher avec ceux qui vous ont liké(e) est réservé aux membres Premium ✨");
      navigate("/premium");
      return;
    }
    setBusy(p.user_id);
    try {
      const { data: res } = await api.post("/swipe", { target_id: p.user_id, action: "like" });
      if (res.premium_required) {
        toast.error("Passez Premium pour matcher avec ceux qui vous ont liké(e).");
        navigate("/premium");
        return;
      }
      setData((d) => ({ ...d, count: Math.max(0, d.count - 1), profiles: d.profiles.filter((x) => x.user_id !== p.user_id) }));
      if (res.matched) {
        setMatch({ profile: res.profile || p, match_id: res.match_id });
      } else {
        toast.success(`Vous avez liké ${p.name} 💛`);
      }
    } catch (e) {
      toast.error("Action impossible.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-kora-sand pb-28">
      <div className="max-w-md mx-auto px-5 pt-6">
        <h1 className="font-display font-black text-3xl text-kora-ink mb-1">Qui m'aime</h1>
        <p className="text-kora-ink/60 mb-6">
          {data.count > 0
            ? (data.premium ? `${data.count} personne(s) vous ont liké(e). Tapez pour matcher !` : `${data.count} personne(s) vous ont liké(e).`)
            : "Personne pour l'instant — continuez à swiper !"}
        </p>

        {!data.premium && data.count > 0 && (
          <button onClick={() => navigate("/premium")} data-testid="likes-premium-cta" className="w-full mb-5 bg-gradient-to-r from-kora-terracotta to-kora-ochre text-white rounded-2xl p-4 text-left animate-kora-pulse">
            <p className="font-display font-bold">Matchez avec eux grâce à Premium ✨</p>
            <p className="text-sm text-white/90">Réservé aux membres Premium : voyez les photos en clair et matchez en un tap avec ceux qui vous ont liké(e).</p>
          </button>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-9 h-9 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.count === 0 ? (
          <div className="bg-white rounded-3xl border border-kora-line p-8 text-center mt-4">
            <div className="w-16 h-16 rounded-full bg-kora-sand flex items-center justify-center mx-auto mb-4">
              <Heart size={28} className="text-kora-terracotta" />
            </div>
            <p className="font-display font-bold text-xl text-kora-ink">Aucun like reçu</p>
            <button onClick={() => navigate("/decouvrir")} className="mt-5 bg-kora-terracotta text-white rounded-full px-6 py-3 font-semibold">Découvrir</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {data.profiles.map((p) => (
              <button
                key={p.user_id}
                data-testid="like-card"
                onClick={() => likeBack(p)}
                disabled={busy === p.user_id}
                className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-gray-900 text-left hover:-translate-y-1 transition-transform disabled:opacity-70"
              >
                <img src={resolveImg(p.photo_url)} alt={p.name} className={`w-full h-full object-cover ${!data.premium ? "blur-xl scale-110" : ""}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-kora-ink/90 to-transparent" />
                {p.is_super && (
                  <span data-testid="super-badge" className="absolute top-2 left-2 flex items-center gap-1 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    <Star size={10} fill="white" /> Super
                  </span>
                )}
                {!data.premium && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock size={28} className="text-white/90" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 w-full p-3 text-white flex items-end justify-between">
                  <div>
                    {data.premium ? (
                      <p className="font-display font-bold">{p.name}, {p.age}</p>
                    ) : (
                      <p className="font-display font-bold blur-[3px]">●●●●, {p.age}</p>
                    )}
                    <p className={`text-xs text-white/80 ${!data.premium ? "blur-[3px]" : ""}`}>{data.premium ? p.city : "●●●●●"}</p>
                  </div>
                  <span data-testid="likeback-btn" className="w-10 h-10 rounded-full bg-kora-terracotta flex items-center justify-center shrink-0 shadow-lg">
                    {busy === p.user_id
                      ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Heart size={18} className="text-white" fill="white" />}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {match && (
          <MatchModal
            profile={match.profile}
            onClose={() => setMatch(null)}
            onChat={() => navigate(`/chat/${match.match_id}`)}
          />
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default Likes;
